import type { HttpClient } from './http.js'
import { readKey, writeKey, type KeyValueStore } from './storage.js'
import { derived, patch, readonly, writable, type ReadableStore } from './store.js'

/**
 * Who is using the client: an account, or a guest with a room-scoped pass.
 *
 * The access token stays inside the HTTP client, in memory. On a cold load
 * it is gone, but the httpOnly refresh cookie may still be valid, so
 * `restore()` tries once, quietly.
 */

export interface User {
  id: string
  email: string
  displayName: string
  avatarUrl?: string
}

export interface AuthState {
  user: User | null
  /** A guest's name, remembered across visits so the next join is one click. */
  guestName: string | null
  /** A guest's pass for one room; null for accounts. */
  guestToken: string | null
  /** True until the first `restore()` has settled. */
  restoring: boolean
  /** The last sign-in or guest-pass failure, for showing. */
  error: string | null
}

interface AuthResponse {
  user: User
  accessToken: string
}

const GUEST_NAME_KEY = 'ls_guest_name'

export class Auth {
  private readonly store: ReturnType<typeof writable<AuthState>>
  /** The current identity. */
  readonly state: ReadableStore<AuthState>
  /** The name shown for this client: the account's, the guest's, or "Guest". */
  readonly name: ReadableStore<string>
  /** True while an account is signed in (a guest's pass does not count). */
  readonly signedIn: ReadableStore<boolean>

  constructor(
    private readonly http: HttpClient,
    private readonly local: KeyValueStore | null,
  ) {
    this.store = writable<AuthState>({
      user: null,
      guestName: readKey(local, GUEST_NAME_KEY),
      guestToken: null,
      restoring: true,
      error: null,
    })
    this.state = readonly(this.store)
    this.name = derived([this.store], (s) => s.user?.displayName ?? s.guestName ?? 'Guest')
    this.signedIn = derived([this.store], (s) => s.user !== null)
    http.onUnauthenticated(() => patch(this.store, { user: null }))
  }

  get isAuthenticated(): boolean {
    return this.store.get().user !== null
  }

  /** The current value of `name`. */
  get displayName(): string {
    return this.name.get()
  }

  /**
   * What the socket handshake presents, asked on every connection attempt:
   * an account's access token refreshed if it is about to expire, else a
   * guest's pass.
   */
  socketToken = (): Promise<string | null> | string | null =>
    this.isAuthenticated ? this.http.freshAccessToken() : this.store.get().guestToken

  async restore(): Promise<void> {
    patch(this.store, { restoring: true })
    try {
      this.adopt(await this.http.post<AuthResponse>('/auth/refresh'))
    } catch {
      patch(this.store, { user: null })
    } finally {
      patch(this.store, { restoring: false })
    }
  }

  /** Create an account; false with `state.error` set when it could not. */
  register(email: string, password: string, displayName: string): Promise<boolean> {
    return this.attempt('Could not create the account.', () =>
      this.http.post<AuthResponse>('/auth/register', { email, password, displayName }),
    )
  }

  login(email: string, password: string): Promise<boolean> {
    return this.attempt('Could not sign in.', () =>
      this.http.post<AuthResponse>('/auth/login', { email, password }),
    )
  }

  async logout(): Promise<void> {
    try {
      await this.http.post('/auth/logout')
    } finally {
      this.http.setAccessToken(null)
      patch(this.store, { user: null, guestToken: null })
    }
  }

  /** Exchange a display name for a room-scoped pass. No account needed. */
  async claimGuestPass(slug: string, name: string): Promise<boolean> {
    try {
      const result = await this.http.post<{ token: string }>(`/rooms/${slug}/guest-token`, {
        displayName: name,
      })
      writeKey(this.local, GUEST_NAME_KEY, name)
      patch(this.store, { guestToken: result.token, guestName: name, error: null })
      return true
    } catch (err) {
      patch(this.store, { error: messageOf(err, 'Could not join as a guest.') })
      return false
    }
  }

  /** Reload the signed-in user, after a profile change. */
  async me(): Promise<void> {
    const result = await this.http.get<{ user: User }>('/auth/me')
    patch(this.store, { user: result.user })
  }

  private adopt(result: AuthResponse): void {
    this.http.setAccessToken(result.accessToken)
    patch(this.store, { user: result.user, error: null })
  }

  private async attempt(fallback: string, call: () => Promise<AuthResponse>): Promise<boolean> {
    try {
      this.adopt(await call())
      return true
    } catch (err) {
      patch(this.store, { error: messageOf(err, fallback) })
      return false
    }
  }
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback
